import ChevronIcon from '~/assets/icons/chevron.svg';
import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { t } from 'ttag';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Flex } from '~/ui/flex/Flex';
import { HelpPages, useHelpStore } from './Store';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import PointOfContact from '~/ui/point-of-contact/PointOfContact';

const Chevron = styled(ChevronIcon)`
  margin-left: 8px;
  stroke: var(--color-black);
  flex-shrink: 0;
`;

const MenuItem = styled.button`
  border: none;
  background: none;
  display: flex;
  /* CAT / WCAG 1.4.10: fixed 48px clipped wrapped labels at 400% zoom and
     painted text outside the LlamaModal chrome (overflow: visible). */
  min-height: 48px;
  height: auto;
  text-align: left;
  padding: 8px 0;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.colors.black};
  border-radius: 4px;
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  > span {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const HelpBody = styled(Flex)`
  /* Override Flex list's height:100% so tall content grows ModalContent's
     scrollHeight instead of painting past the modal background. */
  height: auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'featureShowHotline',
    'featureShowAbout',
    'featureShowIssueReporting',
    'feedbackUrl'
  );

export const Root = () => {
  const { page, setPage, toggle, open, returnFocusTo } = useHelpStore();
  const {
    featureShowHotline,
    featureShowAbout,
    featureShowIssueReporting,
    feedbackUrl
  } = useTrainingStore(selector);

  const { addBlocker, removeBlocker } = useSequenceStore();

  const faqRef = useRef<HTMLButtonElement>(null);
  const hotlineRef = useRef<HTMLButtonElement>(null);
  const reportIssueRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    addBlocker('nextBtn');
    return () => removeBlocker('nextBtn');
  }, [open, page]);

  useLayoutEffect(() => {
    if (page !== HelpPages.Root || returnFocusTo == null) return;

    const targetByPage: Partial<
      Record<HelpPages, React.RefObject<HTMLButtonElement>>
    > = {
      [HelpPages.FAQ]: faqRef,
      [HelpPages.ConfidentialHotline]: hotlineRef,
      [HelpPages.ReportIssue]: reportIssueRef
    };

    const target =
      targetByPage[returnFocusTo]?.current ??
      document.querySelector<HTMLElement>(
        `[data-help-menu-item="${returnFocusTo}"]`
      );

    target?.focus();
  }, [page, returnFocusTo]);

  if (page !== HelpPages.Root) return null;

  return (
    <>
      <CloseButton
        autoFocus={returnFocusTo == null}
        onClick={() => toggle(false)}
      />
      <HelpBody variant="list">
        <x.h2 ml="30px" fontFamily="llama">{t`How can I help?`}</x.h2>
        <MenuItem
          ref={faqRef}
          data-help-menu-item={HelpPages.FAQ}
          onClick={() => setPage(HelpPages.FAQ)}
        >
          <x.span flex="1">{t`Visit the EasyLlama FAQ`}</x.span>
          <Chevron />
        </MenuItem>
        {featureShowHotline && feedbackUrl && (
          <MenuItem
            onClick={() => {
              window.open(feedbackUrl, '_blank');
            }}
          >
            <x.span flex="1">{t`Send anonymous feedback to your organization`}</x.span>
            <Chevron />
          </MenuItem>
        )}
        {featureShowHotline && !feedbackUrl && (
          <MenuItem
            ref={hotlineRef}
            data-help-menu-item={HelpPages.ConfidentialHotline}
            onClick={() => setPage(HelpPages.ConfidentialHotline)}
          >
            <x.span flex="1">{t`Ask your organization a question`}</x.span>
            <Chevron />
          </MenuItem>
        )}
        {featureShowIssueReporting && (
          <MenuItem
            ref={reportIssueRef}
            data-help-menu-item={HelpPages.ReportIssue}
            onClick={() => setPage(HelpPages.ReportIssue)}
          >
            <x.span flex="1">{t`Report an issue with this slide`}</x.span>
            <Chevron />
          </MenuItem>
        )}
        {featureShowAbout && (
          <MenuItem
            onClick={() => {
              window.open('https://www.easyllama.com', '_blank');
            }}
          >
            <x.span flex="1">{t`About EasyLlama`}</x.span>
            <Chevron />
          </MenuItem>
        )}
        <PointOfContact />
      </HelpBody>
    </>
  );
};
